import { Modal } from '@pmi/dsm-react';
import { FormikErrors, FormikProps } from 'formik';
import jwtDecode from 'jwt-decode';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toastWithAnalytics } from '../../../../../shared/analytics';
import Banner from '../../../../../shared/components/Banner';
import { useAddProfileAddressMutation } from '../../../../../shared/components/Cart/apiSlice';
import Overlay from '../../../../../shared/components/Overlay';
import { isPayByUserCartStatus } from '../../../../../shared/constants';
import { getRazorpayApiKey } from '../../../../../shared/env-vars';
import useSchemaValidator from '../../../../../shared/hooks/useSchemaValidator';
import {
  AdyenDropinElement,
  PaymentVendors,
} from '../../../../../shared/models/PaymentModels';
import {
  Address,
  PhoneNumber,
} from '../../../../../shared/models/ProfileModel';
import { JwtDecodedUser } from '../../../../../shared/models/UserModels';
import { userSelector } from '../../../../../shared/store/shared/userSlice';
import { isBundleOptionChapter } from '../../../helpers';
import { Product, ProductBundleOption, ProductLink } from '../../../models';
import {
  PhoneNumberInput,
  SelectOption,
} from '../../../../../shared/components/PhoneNumberForm/models';
import BundleOptionSelector, {
  BundleOptionSelections,
} from '../../BundleOptionSelector';
import {
  useCreateAdyenPaymentMutation,
  useCreateRazorpayOrderMutation,
  useRedemptionEnquiryMutation,
  useSetPrimaryPaymentMethodMutation,
  useStatesQueryMutation,
  useUpdateProfileMutation,
} from '../apiSlice';
import {
  LocalChapter,
  ProfileModel,
  RedemptionProfile,
  RedemptionProfileEnquiry,
} from '../ProfileUpdateModal/models';
import { SavedPayment } from '../models';
import { BillingAddressDetails } from './BillingAddress';
import { Chapter } from './ChapterSelectionBlock';
import AutoRenewFooterActions from './components/AutoRenewFooterActions';
import AutoRenewPaymentStep from './components/AutoRenewPaymentStep';
import BenefitsPanel from './components/BenefitsPanel';
import ChapterSelectionSection from './components/ChapterSelectionSection';
import ProductHeaderSection from './components/ProductHeaderSection';
import ProfileUpdateSection from './components/ProfileUpdateSection';
import styles from './enhancedEnrollmentModal.module.scss';
import {
  findPrimaryAddress,
  getBundleOptionsWithSelectedChapter,
  getInitialChapterSelection,
  getSelectableBundleOptions,
  getSuggestedChapters,
  isMarExperience,
} from './helpers';

export interface EnhanceEnrollmentModalProps {
  accountId: string;
  product: Product;
  onCancel: (report?: boolean) => void;
  onSubmit: (
    bundleOptions?: ProductBundleOption[],
    enrollAutorenew?: boolean,
  ) => void;
  profileData: RedemptionProfileEnquiry;
  localChapters: LocalChapter[];
}

export default function EnhancedEnrollmentModal({
  accountId,
  product,
  onCancel,
  onSubmit,
  profileData,
  localChapters = [],
}: EnhanceEnrollmentModalProps) {
  const formikRef = useRef<FormikProps<ProfileModel>>(undefined);
  const billingAddressFormikRef =
    useRef<FormikProps<BillingAddressDetails>>(undefined);
  const adyenDropinRef = useRef<AdyenDropinElement | null>(null);
  // Using a ref instead of state because we only need the selected payment
  // at submit time — no need to re-render the parent every time the user
  // picks a different card in the child component.
  const selectedPaymentRef = useRef<SavedPayment | 'new' | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<
    SavedPayment | 'new' | null
  >(null);
  const skipAutoRenewRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const { validatePhoneNumber, validateAddress } = useSchemaValidator();
  const [updateProfile, { isLoading: updateProfileLoading }] =
    useUpdateProfileMutation();
  const [addProfileAddress] = useAddProfileAddressMutation();
  const [createAdyenPayment] = useCreateAdyenPaymentMutation();
  const [createRazorpayOrder] = useCreateRazorpayOrderMutation();
  const [setPrimaryPaymentMethod] = useSetPrimaryPaymentMethodMutation();
  const [refreshRedemptionEnquiry] = useRedemptionEnquiryMutation();
  const [statesQuery] = useStatesQueryMutation();
  const razorpayScriptPromiseRef = useRef<Promise<void> | null>(null);
  const [chapterSelected, setChapterSelected] = useState<
    ProductLink | Chapter | null
  >(() => getInitialChapterSelection(product.bundleOptions));
  const [changeChapter, setChangeChapter] = useState(false);
  const [isChapterSearch, setIsChapterSearch] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCountryRestricted, setIsCountryRestricted] = useState(false);
  const [paymentInlineError, setPaymentInlineError] = useState('');
  const [step, setStep] = useState(1);
  const { value: user } = useSelector(userSelector);
  const accessToken = user ? user.access_token : '';
  const authUser: JwtDecodedUser = jwtDecode(accessToken);

  const [sameAsBillingAddress, setSameAsBillingAddress] =
    useState<boolean>(true);
  const sameAsBillingAddressRef = useRef<boolean>(true);
  const razorpayPhoneFormikRef = useRef<
    FormikProps<PhoneNumberInput> | undefined
  >(undefined);
  const [razorpayPhoneInput, setRazorpayPhoneInput] =
    useState<PhoneNumberInput>({ phoneNumber: {} });
  const [billingAddress, setBillingAddress] = useState<BillingAddressDetails>({
    addressLine1: '',
    addressLine2: '',
    city: '',
    country: '',
    state: '',
    stateLabel: '',
    postal: '',
  });
  const [currentRedemptionProfile, setCurrentRedemptionProfile] =
    useState<RedemptionProfile>(profileData.redemptionProfile);
  const [selectionsBySku, setSelectionsBySku] =
    useState<BundleOptionSelections>({});
  const [showSelectionErrors, setShowSelectionErrors] = useState(false);

  const setSubmitting = useCallback((value: boolean) => {
    isSubmittingRef.current = value;
    setIsSubmitting(value);
  }, []);

  const {
    requestPhoneInfo: phoneInputNeeded,
    requestAddressInfo: addressInputNeeded,
    redemptionProfile,
  } = profileData;

  useEffect(() => {
    setCurrentRedemptionProfile(profileData.redemptionProfile);
  }, [profileData.redemptionProfile]);

  // Derived state

  const primaryAddress = useMemo(
    () => findPrimaryAddress(currentRedemptionProfile),
    [currentRedemptionProfile],
  );

  useEffect(() => {
    if (!primaryAddress && sameAsBillingAddress) {
      setSameAsBillingAddress(false);
      sameAsBillingAddressRef.current = false;
    }
  }, [primaryAddress, sameAsBillingAddress]);

  useEffect(() => {
    sameAsBillingAddressRef.current = sameAsBillingAddress;
  }, [sameAsBillingAddress]);

  const bundleContainsCert = product.bundleOptions.some((x) =>
    x.productLinks.some((y) => y.fulfillmentProvider === 'Certification'),
  );

  const refreshRedemptionProfile = async () => {
    try {
      const refreshed = await refreshRedemptionEnquiry({
        productType: bundleContainsCert ? 'Certification' : product.type,
        accountId,
        dealIds: product.dealIds,
      }).unwrap();
      if (refreshed?.redemptionProfile) {
        setCurrentRedemptionProfile(refreshed.redemptionProfile);
        return refreshed.redemptionProfile;
      }
    } catch {
      // Keep proceeding with locally available data if refresh fails.
    }

    return null;
  };

  const chaptersInBundle = useMemo(() => {
    const chapterBundleOptions = product.bundleOptions
      .filter((bo) => isBundleOptionChapter(bo))
      .flatMap((x) => x.productLinks);

    return chapterBundleOptions.map((o) => ({ ...o } as Chapter));
  }, [product.bundleOptions]);
  const isChapterIncluded =
    product.isSingleMembershipEligible && chaptersInBundle?.length > 0;
  const isMar = isMarExperience(product.experienceType);
  const isRazorpayMarFlow = useMemo(() => {
    const rawVendor = product.paymentVendor || '';

    return (
      isMar &&
      rawVendor.trim().toLowerCase() === PaymentVendors.RazorPay.toLowerCase()
    );
  }, [isMar, product]);

  const ensureRazorpaySdkLoaded = useCallback(async () => {
    if ((window as { Razorpay?: unknown }).Razorpay) {
      return;
    }

    if (!razorpayScriptPromiseRef.current) {
      razorpayScriptPromiseRef.current = new Promise<void>(
        (resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://checkout.razorpay.com/v1/checkout.js';
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () =>
            reject(new Error('Failed to load RazorPay SDK.'));
          document.body.appendChild(script);
        },
      );
    }

    await razorpayScriptPromiseRef.current;
  }, []);

  const suggestedChapters = useMemo(
    () =>
      getSuggestedChapters(chapterSelected, chaptersInBundle, localChapters),
    [chapterSelected, chaptersInBundle, localChapters],
  );

  useEffect(() => {
    if (!chapterSelected && suggestedChapters.length > 0) {
      setChapterSelected(suggestedChapters[0]);
    }
  }, [chapterSelected, suggestedChapters]);

  // Bundle/chapter selection

  const selectableBundleOptions = useMemo(
    () => getSelectableBundleOptions(product.bundleOptions),
    [product.bundleOptions],
  );
  const hasSelectableBundleOptions = selectableBundleOptions.length > 0;

  const selectedBundleOptions = useMemo(
    () =>
      getBundleOptionsWithSelectedChapter(
        product.bundleOptions,
        chapterSelected,
        selectionsBySku,
      ),
    [product.bundleOptions, chapterSelected, selectionsBySku],
  );

  const submitProfileFormIfRequired = useCallback(async () => {
    if (!profileData.requestProfileUpdate) {
      return false;
    }

    try {
      await formikRef.current.submitForm();
      return true;
    } catch {
      setSubmitting(false);
      toastWithAnalytics(
        'There was an unexpected error while updating profile details',
        'danger',
      );
      return false;
    }
  }, [profileData.requestProfileUpdate]);

  const validateSelectionFormIfRequired = useCallback(() => {
    if (!hasSelectableBundleOptions) {
      return false;
    }
    const allSelected = selectableBundleOptions.every(
      (option) => !!selectionsBySku[String(option.id)],
    );
    if (!allSelected) {
      setShowSelectionErrors(true);
      setSubmitting(false);
      return true;
    }
    return false;
  }, [hasSelectableBundleOptions, selectableBundleOptions, selectionsBySku]);

  const onProfileFormSubmit = useCallback(
    async (values: ProfileModel) => {
      setSubmitting(true);
      let phoneNumber: PhoneNumber = null;
      if (phoneInputNeeded) {
        phoneNumber = values.phoneNumber;
      }
      let address: Address = null;
      if (addressInputNeeded) {
        address = values.address;
      }
      await updateProfile({
        address,
        phoneNumber,
      }).unwrap();

      await refreshRedemptionProfile();
      if (isMar && !skipAutoRenewRef.current) {
        sameAsBillingAddressRef.current = true;
        setSameAsBillingAddress(true);
        setStep(2);
        setSubmitting(false);
      } else {
        onSubmit(selectedBundleOptions);
      }
    },
    [
      addressInputNeeded,
      phoneInputNeeded,
      refreshRedemptionProfile,
      updateProfile,
    ],
  );

  // Profile update and validation
  const validateProfile = useCallback(
    (values: ProfileModel) => {
      const errors: FormikErrors<ProfileModel> = {};
      const onInvalid = () => {
        const mbody = document.querySelector(
          `.${styles.enhanceEnrollmentModal} .modal-body`,
        );
        mbody.scroll({
          top: mbody.clientHeight,
          behavior: 'smooth',
        });
      };
      try {
        if (phoneInputNeeded) {
          const result = validatePhoneNumber(
            {
              ...values.phoneNumber,
              type: 1, // for preventing validation to fail
              countryId: values.phoneNumber?.countryId
                ? Number(values.phoneNumber?.countryId)
                : null,
            },
            redemptionProfile?.phoneNumbers?._embedded?.schema,
          );
          if (!result.valid) {
            setSubmitting(false);
            errors.phoneNumber = result.errors;
            onInvalid();
          }
        }

        if (addressInputNeeded) {
          const result = validateAddress(
            { ...values.address },
            redemptionProfile?.addresses?._embedded?.schema,
          );
          if (!result.valid) {
            errors.address = result.errors;
            setSubmitting(false);
            onInvalid();
          }
        }
      } catch (error) {
        setSubmitting(false);
        onInvalid();
        toastWithAnalytics('Unexpected Error while validating data', 'danger');
      }
      return errors;
    },
    [
      addressInputNeeded,
      phoneInputNeeded,
      redemptionProfile?.addresses?._embedded?.schema,
      redemptionProfile?.phoneNumbers?._embedded?.schema,
      validateAddress,
      validatePhoneNumber,
    ],
  );
  const onCountryRestricted = useCallback((isRestricted: boolean): void => {
    setIsCountryRestricted(isRestricted);
    const modalFooter: HTMLDivElement = document.querySelector(
      `.${styles.enhanceEnrollmentModal} .modal-footer`,
    );
    if (modalFooter) {
      const elId = `${styles.enhanceEnrollmentModal}-country-restricted`;
      const elDiv = document.querySelector(`[data-id="${elId}"]`);
      if (elDiv) {
        elDiv.remove();
      }
      if (isRestricted) {
        const alert = document.createElement('div');
        alert.setAttribute('data-id', elId);
        alert.classList.add('d-block', 'mb-3', 'w-100', 'text-end');
        alert.innerHTML =
          '<p class="d-block m-0 text-danger">We cannot process enrollments for residents of China. Please select another country.</p>';
        modalFooter.prepend(alert);
      }
    }
  }, []);

  const onSelectChapter = useCallback((chapter: Chapter) => {
    setChapterSelected(chapter);
  }, []);

  const onChangeChapter = useCallback((value: boolean) => {
    setChangeChapter(value);
  }, []);

  const onChapterSearch = useCallback((searchMode: boolean) => {
    setIsChapterSearch(searchMode);
  }, []);

  // Step-specific labels and headers
  const isPbuProduct =
    product.isPayByUserProduct &&
    isPayByUserCartStatus(product.callToAction?.text) &&
    product.price > 0;
  const isStep2 = step === 2;

  const shouldShowProfileUpdateSection =
    profileData.requestProfileUpdate && !product.isPayByUserProduct;

  // Payment and billing

  const onToggleSameAsBillingAddress = useCallback((checked: boolean) => {
    sameAsBillingAddressRef.current = checked;
    setSameAsBillingAddress(checked);
  }, []);

  const validateBillingAddress = useCallback(
    (values: BillingAddressDetails) => {
      const errors: FormikErrors<BillingAddressDetails> = {};
      const addressSchema = redemptionProfile?.addresses?._embedded?.schema;
      if (!addressSchema) {
        return errors;
      }

      const result = validateAddress(
        {
          address1: values.addressLine1,
          address2: values.addressLine2,
          city: values.city,
          state: values.state,
          country: values.country,
          postalCode: values.postal,
        },
        addressSchema,
      );

      if (!result.valid) {
        const addressErrors = result.errors as FormikErrors<Address>;
        errors.addressLine1 = addressErrors?.address1;
        errors.addressLine2 = addressErrors?.address2;
        errors.city = addressErrors?.city;
        errors.country = addressErrors?.country;
        errors.state = addressErrors?.state;
        errors.postal = addressErrors?.postalCode;
      }

      return errors;
    },
    [redemptionProfile?.addresses?._embedded?.schema, validateAddress],
  );

  const validateBillingAddressForPayment = useCallback(async () => {
    if (sameAsBillingAddressRef.current && primaryAddress) {
      return true;
    }

    const form = billingAddressFormikRef.current;
    if (!form) {
      return true;
    }

    const errors = await form.validateForm();
    const hasErrors = Object.keys(errors || {}).length > 0;
    if (hasErrors) {
      form.setTouched(
        {
          addressLine1: true,
          addressLine2: true,
          city: true,
          country: true,
          state: true,
          postal: true,
        },
        false,
      );

      requestAnimationFrame(() => {
        const modalBody = document.querySelector(
          `.${styles.enhanceEnrollmentModal} .modal-body`,
        );
        const firstInvalidField = modalBody?.querySelector(
          '.dsm-input-field__form-control--error, .is-invalid',
        ) as HTMLElement | null;

        if (firstInvalidField) {
          firstInvalidField.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
          firstInvalidField.focus?.();
        }
      });

      return false;
    }

    return true;
  }, [primaryAddress]);

  const getBillingAddressPayload = useCallback(() => {
    if (sameAsBillingAddressRef.current && primaryAddress) {
      return {
        id: primaryAddress.id,
        address1: primaryAddress.address1 || '',
        address2: primaryAddress.address2 || '',
        city: primaryAddress.city || '',
        state: primaryAddress.state || '',
        stateLabel: primaryAddress.stateLabel || '',
        country: primaryAddress.country || '',
        postalCode: primaryAddress.postalCode || '',
      };
    }

    const currentBillingAddress =
      billingAddressFormikRef.current?.values || billingAddress;

    return {
      address1: currentBillingAddress.addressLine1,
      address2: currentBillingAddress.addressLine2,
      city: currentBillingAddress.city,
      state: currentBillingAddress.state,
      stateLabel: currentBillingAddress.stateLabel,
      country: currentBillingAddress.country,
      postalCode: currentBillingAddress.postal,
    };
  }, [billingAddress, primaryAddress]);

  const getRazorpayContact = useCallback(
    (
      phoneNumber: string | undefined,
      countryId?: string | number | null,
    ): string => {
      const numberDigits = (phoneNumber || '').replace(/\D/g, '');
      const countries =
        profileData?.redemptionProfile?.phoneNumbers?._embedded?.countries ||
        [];
      const selectedCountry = countries.find(
        (country: SelectOption) =>
          country.value === String(countryId || '').trim(),
      );
      const countryCodeMatch = selectedCountry?.text?.match(/\+(\d+)/);
      const countryDigits = (countryCodeMatch?.[1] || '').replace(/\D/g, '');

      if (!numberDigits) {
        return '';
      }

      if (!countryDigits) {
        return numberDigits;
      }

      // Avoid duplicating country code when number already includes it.
      if (numberDigits.startsWith(countryDigits)) {
        return numberDigits;
      }

      return `${countryDigits}${numberDigits}`;
    },
    [profileData?.redemptionProfile?.phoneNumbers?._embedded?.countries],
  );

  const getProfilePhoneContact = useCallback(() => {
    const phoneNumbers = currentRedemptionProfile?.phoneNumbers?.items || [];
    const primaryPhone =
      phoneNumbers.find(
        (phoneNumber) => phoneNumber.primary && phoneNumber.number,
      ) || phoneNumbers.find((phoneNumber) => phoneNumber.number);

    return getRazorpayContact(primaryPhone?.number, primaryPhone?.countryId);
  }, [currentRedemptionProfile, getRazorpayContact]);

  const shouldRequestRazorpayPhoneNumber = useMemo(
    () => isRazorpayMarFlow && !getProfilePhoneContact(),
    [isRazorpayMarFlow, getProfilePhoneContact],
  );

  const validateRazorpayPhoneNumber = useCallback(
    (values: PhoneNumberInput): FormikErrors<PhoneNumberInput> => {
      const errors: FormikErrors<PhoneNumberInput> = {};
      const result = validatePhoneNumber(
        {
          ...values.phoneNumber,
          type: 1,
          countryId: values.phoneNumber?.countryId
            ? Number(values.phoneNumber?.countryId)
            : null,
        },
        redemptionProfile?.phoneNumbers?._embedded?.schema,
      );
      if (!result.valid) {
        errors.phoneNumber = result.errors;
      }
      return errors;
    },
    [redemptionProfile?.phoneNumbers?._embedded?.schema, validatePhoneNumber],
  );

  const ensureRazorpayPrimaryPhoneBeforeOrder = useCallback(async () => {
    if (!shouldRequestRazorpayPhoneNumber) {
      return getProfilePhoneContact();
    }

    if (!razorpayPhoneFormikRef.current) {
      setPaymentInlineError(
        'Please enter a valid phone number before continuing with auto-renew.',
      );
      return '';
    }

    const phoneErrors = await razorpayPhoneFormikRef.current.validateForm();
    razorpayPhoneFormikRef.current.setTouched(
      { phoneNumber: { number: true, countryId: true } },
      false,
    );

    if (Object.keys(phoneErrors).length > 0) {
      setPaymentInlineError(
        'Please enter a valid phone number before continuing with auto-renew.',
      );
      return '';
    }

    const enteredPhone = razorpayPhoneFormikRef.current.values?.phoneNumber;
    const normalizedCountryId = enteredPhone?.countryId
      ? Number(enteredPhone.countryId)
      : null;

    if (!enteredPhone?.number || !normalizedCountryId) {
      setPaymentInlineError(
        'Please enter a valid phone number before continuing with auto-renew.',
      );
      return '';
    }

    const phonePayload: PhoneNumber = {
      ...enteredPhone,
      countryId: normalizedCountryId,
      primary: true,
      type: 1,
    };

    await updateProfile({
      address: null,
      phoneNumber: phonePayload,
    }).unwrap();

    setRazorpayPhoneInput({ phoneNumber: phonePayload });

    const refreshedProfile = await refreshRedemptionProfile();
    const refreshedPrimaryPhone =
      refreshedProfile?.phoneNumbers?.items?.find(
        (phoneNumber) => phoneNumber.primary && phoneNumber.number,
      ) ||
      refreshedProfile?.phoneNumbers?.items?.find(
        (phoneNumber) => phoneNumber.number,
      );

    return getRazorpayContact(
      refreshedPrimaryPhone?.number || phonePayload.number,
      refreshedPrimaryPhone?.countryId || phonePayload.countryId,
    );
  }, [
    getProfilePhoneContact,
    getRazorpayContact,
    refreshRedemptionProfile,
    shouldRequestRazorpayPhoneNumber,
    updateProfile,
  ]);

  const hydrateBillingAddressWithId = useCallback(async () => {
    const payload = getBillingAddressPayload();

    if (payload.id) {
      return payload;
    }

    const createdAddress = await addProfileAddress({
      address1: payload.address1,
      address2: payload.address2,
      city: payload.city,
      state: payload.state,
      country: payload.country,
      postalCode: payload.postalCode,
      primary: false,
    }).unwrap();

    return {
      ...payload,
      id: createdAddress?.id,
    };
  }, [addProfileAddress, getBillingAddressPayload]);

  const hydrateBillingAddressForRazorpay = useCallback(async () => {
    const payload = await hydrateBillingAddressWithId();

    const normalizedState = payload.state?.trim().toLowerCase() || '';
    const normalizedStateLabel = payload.stateLabel?.trim().toLowerCase() || '';
    const hasResolvedStateLabel =
      !!normalizedStateLabel && normalizedStateLabel !== normalizedState;

    if (hasResolvedStateLabel) {
      return payload;
    }

    if (!payload.country || !payload.state?.trim()) {
      return payload;
    }

    try {
      const states = await statesQuery({
        country: payload.country,
      }).unwrap();

      const matchedState = states?.find(
        (option) =>
          option.value?.trim().toLowerCase() === normalizedState ||
          option.text?.trim().toLowerCase() === normalizedState,
      );

      if (matchedState?.text) {
        return {
          ...payload,
          stateLabel: matchedState.text,
        };
      }
    } catch {
      // Fall through and use the original payload if state lookup fails.
    }

    return payload;
  }, [hydrateBillingAddressWithId, statesQuery]);

  const onAdyenSubmit = useCallback(
    async (state: any, _component: any, actions: any) => {
      try {
        setPaymentInlineError('');

        if (!(await validateBillingAddressForPayment())) {
          actions.reject();
          return;
        }

        setIsProcessing(true);
        const hydratedBillingAddress = await hydrateBillingAddressWithId();
        const response = await createAdyenPayment({
          state: {
            ...state,
            data: {
              ...state?.data,
              paymentMethod: {
                ...state?.data?.paymentMethod,
                tokenize: true,
                storeAsPreferred: true,
                storePaymentMethod: true,
              },
            },
          },
          billingAddress: hydratedBillingAddress,
        }).unwrap();

        const status =
          (response as { status?: string })?.status ||
          (response as { vendorData?: { status?: string } })?.vendorData
            ?.status;
        const normalizedStatus = status?.toLowerCase();
        const isAuthorized =
          normalizedStatus === 'authorised' ||
          normalizedStatus === 'authorized';

        if (!isAuthorized) {
          const refusalMessage =
            (response as { refusalReason?: { message?: string } })
              ?.refusalReason?.message || '';
          setIsProcessing(false);
          setPaymentInlineError(
            refusalMessage
              ? `Your payment method could not be authorized: ${refusalMessage}`
              : 'Your payment method could not be authorized. Please review your payment details or use a different card.',
          );
          actions.reject();
          return;
        }

        actions.resolve(response as any);
        onSubmit(selectedBundleOptions, true);
      } catch {
        setIsProcessing(false);
        setPaymentInlineError(
          'There was an error saving your payment method. Please try again.',
        );
        actions.reject();
        toastWithAnalytics(
          'There was an error saving your payment method. Please try again.',
          'danger',
        );
      }
    },
    [
      createAdyenPayment,
      hydrateBillingAddressWithId,
      onSubmit,
      selectedBundleOptions,
      validateBillingAddressForPayment,
    ],
  );

  const startRazorpayCheckout = useCallback(async () => {
    try {
      setPaymentInlineError('');

      const contact = await ensureRazorpayPrimaryPhoneBeforeOrder();
      if (!contact) {
        setSubmitting(false);
        return;
      }

      if (!(await validateBillingAddressForPayment())) {
        return;
      }

      setSubmitting(true);
      const hydratedBillingAddress = await hydrateBillingAddressForRazorpay();

      const razorpayOrderRequest = {
        billingAddress: hydratedBillingAddress,
        contact,
      } as any;

      const orderResponse = await createRazorpayOrder(
        razorpayOrderRequest,
      ).unwrap();

      const orderId = (orderResponse as { id?: string })?.id;
      const customerId =
        (orderResponse as { customer?: { id?: string } })?.customer?.id || '';

      if (!orderId || !customerId) {
        console.error(
          'RazorPay order bootstrap did not return order details:',
          orderResponse,
        );
        console.info('RazorPay order request:', orderId, customerId);
        throw new Error(
          'RazorPay order bootstrap did not return order details.',
        );
      }

      await ensureRazorpaySdkLoaded();
      const apiKey = getRazorpayApiKey();
      const RazorpayCtor = (window as { Razorpay?: any }).Razorpay;

      if (!apiKey || !RazorpayCtor) {
        throw new Error('RazorPay configuration is missing.');
      }

      const razorpay = new RazorpayCtor({
        key: apiKey,
        order_id: orderId,
        customer_id: customerId,
        amount: 0,
        currency: 'INR',
        name: 'PMI',
        description: 'Add Payment Method',
        prefill: {
          contact,
          method: 'card',
        },
        recurring: 1,
        method: {
          card: true,
          upi: false,
          wallet: false,
          netbanking: false,
          emi: false,
          paylater: false,
        },
        notes: {
          client: 'MAR',
          zeroAuth: true,
          tokenize: true,
          paymentMethod: 'card',
          invoice_number: orderId,
        },
        handler: () => {
          setIsProcessing(true);
          onSubmit(selectedBundleOptions, true);
        },
        modal: {
          ondismiss: () => {
            setSubmitting(false);
            setIsProcessing(false);
          },
        },
      });

      setSubmitting(false);
      razorpay.open();
    } catch {
      setSubmitting(false);
      setIsProcessing(false);
      setPaymentInlineError(
        'There was an error saving your payment method. Please try again.',
      );
      toastWithAnalytics(
        'There was an error saving your payment method. Please try again.',
        'danger',
      );
    }
  }, [
    createRazorpayOrder,
    ensureRazorpayPrimaryPhoneBeforeOrder,
    ensureRazorpaySdkLoaded,
    hydrateBillingAddressForRazorpay,
    onSubmit,
    selectedBundleOptions,
    validateBillingAddressForPayment,
    setSubmitting,
  ]);

  const onPaymentMethodSelected = useCallback(() => {
    setPaymentInlineError('');
  }, []);

  const onPaymentChange = useCallback(
    (payment: SavedPayment | 'new' | null) => {
      selectedPaymentRef.current = payment;
      setSelectedPayment(payment);
    },
    [],
  );

  const autoRenewButtonText = useMemo(() => {
    if (!isStep2) {
      return 'Redeem with Auto-Renew';
    }

    if (isRazorpayMarFlow && selectedPayment === 'new') {
      return 'Continue To Payment Details';
    }

    return 'Confirm Membership Redemption';
  }, [isRazorpayMarFlow, isStep2, selectedPayment]);

  // Enrollment actions

  const onConfirmClick = async () => {
    if (isSubmittingRef.current) return;
    setSubmitting(true);
    if (isChapterIncluded && !chapterSelected) {
      toastWithAnalytics('Please specify your chapter.', 'danger');
      setSubmitting(false);
      return;
    }

    if (validateSelectionFormIfRequired()) {
      return;
    }

    if (product.isPayByUserProduct) {
      setIsProcessing(true);
      onSubmit(selectedBundleOptions);
      return;
    }

    if (await submitProfileFormIfRequired()) {
      return;
    }
    setIsProcessing(true);
    onSubmit(selectedBundleOptions);
  };

  const handleAutoRenewStepClick = async () => {
    if (isStep2) {
      // add api call here for confirming auto-renew
      setPaymentInlineError('');

      if (!selectedPaymentRef.current) {
        setPaymentInlineError(
          'You must select a payment method to continue with membership redemption.',
        );
        return;
      }

      // If a saved payment method is selected, use it directly
      const selectedPaymentMethod = selectedPaymentRef.current;
      if (selectedPaymentMethod && selectedPaymentMethod !== 'new') {
        const { paymentVendor, pmiAddressId, pmiPaymentId, tokenId } =
          selectedPaymentMethod;
        try {
          setIsProcessing(true);
          await setPrimaryPaymentMethod({
            accountReferenceId: authUser.id ? parseInt(authUser.id, 10) : 0,
            paymentVendor,
            pmiAddressId,
            pmiPaymentId,
            tokenId,
            isDefault: true,
          }).unwrap();

          onSubmit(selectedBundleOptions, true);
        } catch {
          setIsProcessing(false);
          setPaymentInlineError(
            'There was an error saving your payment method. Please try again.',
          );
        }
        return;
      }

      if (isRazorpayMarFlow) {
        await startRazorpayCheckout();
        return;
      }

      if (!(await validateBillingAddressForPayment())) {
        return;
      }

      // Submit via Adyen dropin for new payment method
      if (!adyenDropinRef.current) {
        toastWithAnalytics(
          'Payment form is still loading. Please wait a moment and try again.',
          'danger',
        );
        return;
      }

      adyenDropinRef.current?.submit();
    } else {
      // set step to 2 to show the auto-renew benefits and confirmation
      if (validateSelectionFormIfRequired()) {
        return;
      }
      if (await submitProfileFormIfRequired()) {
        return;
      }
      await refreshRedemptionProfile();
      setStep(2);
    }
  };
  return (
    <>
      <Modal
        buttons={[
          {
            titleText: 'Cancel',
            variant: 'outline-secondary',
            onClick: () => {
              onCancel();
            },
            disabled: updateProfileLoading || isSubmitting,
          },
          {
            titleText: isPbuProduct
              ? 'Confirm and add to cart'
              : 'Confirm and enroll',
            variant: 'primary',
            onClick: () => onConfirmClick(),
            disabled:
              updateProfileLoading ||
              (isChapterIncluded && !chapterSelected) ||
              isSubmitting ||
              isCountryRestricted,
          },
        ]}
        backdrop="static"
        onClose={() => onCancel()}
        variant="input"
        customClassName={`${styles.enhanceEnrollmentModal}${
          isMar ? ` ${styles.customStyleEnabled}` : ''
        }`}
        show={!isProcessing}
      >
        {isStep2 ? (
          <AutoRenewPaymentStep
            onCancel={onCancel}
            requestAddressInfo={profileData.requestAddressInfo}
            primaryAddress={primaryAddress}
            sameAsBillingAddress={sameAsBillingAddress}
            onToggleSameAsBillingAddress={onToggleSameAsBillingAddress}
            countries={
              profileData?.redemptionProfile?.addresses?._embedded?.countries
            }
            billingAddress={billingAddress}
            setBillingAddress={setBillingAddress}
            validateBillingAddress={validateBillingAddress}
            billingAddressFormikRef={billingAddressFormikRef}
            adyenDropinRef={adyenDropinRef}
            onAdyenSubmit={onAdyenSubmit}
            paymentInlineError={paymentInlineError}
            useRazorpay={isRazorpayMarFlow}
            shouldRequestRazorpayPhoneNumber={shouldRequestRazorpayPhoneNumber}
            razorpayPhoneCountries={
              profileData?.redemptionProfile?.phoneNumbers?._embedded?.countries
            }
            razorpayPhoneInput={razorpayPhoneInput}
            setRazorpayPhoneInput={setRazorpayPhoneInput}
            validateRazorpayPhoneNumber={validateRazorpayPhoneNumber}
            razorpayPhoneFormikRef={razorpayPhoneFormikRef}
            onPaymentChange={onPaymentChange}
            onPaymentMethodSelected={onPaymentMethodSelected}
          />
        ) : (
          <>
            {!isMar && <ProductHeaderSection product={product} />}

            {isMar && (
              <BenefitsPanel productName={product.name} onCancel={onCancel} />
            )}
            {hasSelectableBundleOptions && (
              <BundleOptionSelector
                selectableBundleOptions={selectableBundleOptions}
                selections={selectionsBySku}
                onChange={(next) => {
                  setSelectionsBySku(next);
                  if (showSelectionErrors) {
                    setShowSelectionErrors(false);
                  }
                }}
                showErrors={showSelectionErrors}
              />
            )}
            {isChapterIncluded && (
              <ChapterSelectionSection
                chapterSelected={chapterSelected}
                changeChapter={changeChapter}
                isChapterSearch={isChapterSearch}
                chaptersInBundle={chaptersInBundle}
                suggestedChapters={suggestedChapters}
                onChangeChapter={onChangeChapter}
                onChapterSearch={onChapterSearch}
                onSelectChapter={onSelectChapter}
              />
            )}

            {shouldShowProfileUpdateSection && (
              <ProfileUpdateSection
                productType={product.type}
                formikRef={formikRef}
                requestPhoneInfo={profileData.requestPhoneInfo}
                requestAddressInfo={profileData.requestAddressInfo}
                userRedemptionProfile={profileData.redemptionProfile}
                onProfileFormSubmit={onProfileFormSubmit}
                validateProfile={validateProfile}
                onCountryRestricted={onCountryRestricted}
              />
            )}
            {isMar && (
              <div className={styles.bannerWrapper}>
                <Banner
                  type="info"
                  text1="Turn on automatic renewal so you never miss a minute of member benefits."
                  text2="Cancel anytime. Your privacy is our priority."
                />
              </div>
            )}
          </>
        )}
        {isMar && (
          <AutoRenewFooterActions
            autoRenewButtonText={autoRenewButtonText}
            onRedeemNowAndRenewLater={() => {
              skipAutoRenewRef.current = true;
              onConfirmClick();
            }}
            onAutoRenewAction={handleAutoRenewStepClick}
            disabled={isSubmitting || isProcessing}
          />
        )}
      </Modal>
      <Overlay
        variant="PMIOverlay"
        global
        animated
        fade
        iconColor="brand-dark"
        overlayColor="black"
        overlayOpacity={1}
        show={isProcessing}
      />
    </>
  );
}
